<script lang="ts">
	import { Tabs } from "flowbite-svelte-v1";
	import type { ComponentProps } from "svelte";

	type Props = ComponentProps<typeof Tabs>;

	let { children, class: className, tabStyle = "underline", ...rest }: Props = $props();
</script>

<div class="ui-tabs" class:ui-tabs--underline={tabStyle === "underline"}>
	<Tabs {tabStyle} class={className} {...rest}>
		{@render children?.()}
	</Tabs>
</div>

<style>
	/*
	 * display:contents keeps this wrapper out of layout — the flowbite <ul>, divider
	 * and tabpanel behave as if they were direct children of the caller — while still
	 * anchoring the scoped selectors below onto flowbite's rendered DOM. All tab styling
	 * lives here instead of in global CSS.
	 */
	.ui-tabs {
		display: contents;
	}

	/*
	 * Continuous 1px divider under underline tabs. The tabpanel's sticky content header
	 * (bg-white, z-10) otherwise paints over the sub-pixel h-px divider except at the
	 * padded edges. Lift the divider above that header, and the tablist above the divider
	 * so the active tab's 2px underline stays on top.
	 */
	.ui-tabs--underline :global(ul[role="tablist"]) {
		position: relative;
		z-index: 12;
	}

	.ui-tabs--underline :global(ul[role="tablist"] + div:not([role="tabpanel"])) {
		position: relative;
		z-index: 11;
	}

	/* Size icons rendered inside tab titles. */
	.ui-tabs :global(button[role="tab"] svg) {
		height: 1.25rem;
		width: 1.25rem;
	}

	/*
	 * Let tab panel content fill the available height. Scope to divs that actually
	 * contain elements (:has(> *)) so Svelte's empty fragment-anchor divs — which v1
	 * renders as siblings of the content — don't also get full height and push the
	 * real content down.
	 */
	.ui-tabs :global(div[role="tabpanel"] > div:has(> *)) {
		height: 100%;
	}
</style>
